import React, { Component } from 'react';
import './Contact.css';
import logo from './logo.png';
import { Button, Input, Textarea, Field } from '@fluentui/react-components';
import { 
  Navigation24Regular,
  SearchInfoFilled,
  OrganizationFilled,
  ScalesFilled,
  MailFilled,
  SearchFilled,
  PeopleFilled,
  SendFilled
} from '@fluentui/react-icons';
import NavBar from './NavBar';

interface ContactProps {}

interface ContactState {
  isMenuOpen: boolean;
  isProductsMenuOpen: boolean;
  isSolutionsMenuOpen: boolean;
  formData: {
    fullName: string;
    telephone: string;
    country: string;
    company: string;
    email: string;
    address: string;
    website: string;
    comments: string;
  };
  isSubmitting: boolean;
  submitSuccess: boolean;
  submitError: string;
}

class Contact extends Component<ContactProps, ContactState> {
  constructor(props: ContactProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isProductsMenuOpen: false,
      isSolutionsMenuOpen: false,
      formData: {
        fullName: '',
        telephone: '',
        country: '',
        company: '',
        email: '',
        address: '',
        website: '',
        comments: ''
      },
      isSubmitting: false,
      submitSuccess: false,
      submitError: ''
    };
  }

  toggleMenu = () => {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  toggleProductsMenu = () => {
    this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  };

  closeProductsMenu = () => {
    this.setState({ isProductsMenuOpen: false });
  };

  toggleSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: !this.state.isSolutionsMenuOpen });
  };

  closeSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: false });
  };

  handleInputChange = (field: keyof ContactState['formData'], value: string) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [field]: value
      }
    }));
  };

  handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    this.setState({ isSubmitting: true, submitError: '', submitSuccess: false });

    try {
      const powerAutomateUrl = 'https://default705c6e47bdf34fc09bad84965f7302.fd.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/1152e6db518d42918b8a115cc6d81ab9/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=5thbTkaxixmMM7gwiYIA2tBS5J5aVms5cM-NqE775F8';
      
      // Prepare data according to Power Automate schema
      const payload = {
        fullName: this.state.formData.fullName,
        email: this.state.formData.email,
        phone: this.state.formData.telephone,
        address: this.state.formData.address,
        country: this.state.formData.country,
        company: this.state.formData.company,
        website: this.state.formData.website,
        comments: this.state.formData.comments
      };
      
      // Send data to Power Automate
      const response = await fetch(powerAutomateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      // Show success message and reset form
      this.setState({ 
        submitSuccess: true, 
        isSubmitting: false,
        formData: {
          fullName: '',
          telephone: '',
          country: '',
          company: '',
          email: '',
          address: '',
          website: '',
          comments: ''
        }
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        this.setState({ submitSuccess: false });
      }, 5000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      this.setState({ 
        submitError: 'An error occurred while submitting the form. Please try again.',
        isSubmitting: false 
      });
    }
  };

  render() {
    const { formData, isSubmitting, submitSuccess, submitError } = this.state;

    return (
      <div className="contact-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="contact-hero-section">
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">Get in Touch</h1>
            <p className="contact-hero-subtitle">
              Have questions about our products or need assistance? We're here to help. 
              Fill out the form below and our team will get back to you shortly.
            </p>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="contact-form-section">
          <div className="contact-container">
            <div className="contact-form-wrapper">
              <div className="contact-form-header">
                <h2>Contact Form</h2>
                <p>Please provide your information and we'll respond as soon as possible.</p>
              </div>

              <form onSubmit={this.handleSubmit} className="contact-form">
                <div className="form-row">
                  <Field label="Full Name" required className="form-field">
                    <Input 
                      value={formData.fullName}
                      onChange={(e) => this.handleInputChange('fullName', e.target.value)}
                      placeholder="Enter your full name"
                      required
                      size="large"
                    />
                  </Field>

                  <Field label="Email" required className="form-field">
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => this.handleInputChange('email', e.target.value)}
                      placeholder="your.email@example.com"
                      required
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Telephone" required className="form-field">
                    <Input 
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => this.handleInputChange('telephone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      required
                      size="large"
                    />
                  </Field>

                  <Field label="Country" required className="form-field">
                    <Input 
                      value={formData.country}
                      onChange={(e) => this.handleInputChange('country', e.target.value)}
                      placeholder="Enter your country"
                      required
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Company" required className="form-field">
                    <Input 
                      value={formData.company}
                      onChange={(e) => this.handleInputChange('company', e.target.value)}
                      placeholder="Enter your company name"
                      required
                      size="large"
                    />
                  </Field>

                  <Field label="Website" className="form-field">
                    <Input 
                      type="url"
                      value={formData.website}
                      onChange={(e) => this.handleInputChange('website', e.target.value)}
                      placeholder="https://www.yourcompany.com"
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Address" className="form-field full-width">
                    <Input 
                      value={formData.address}
                      onChange={(e) => this.handleInputChange('address', e.target.value)}
                      placeholder="Enter your address"
                      size="large"
                    />
                  </Field>
                </div>

                <div className="form-row">
                  <Field label="Comments" required className="form-field full-width">
                    <Textarea 
                      value={formData.comments}
                      onChange={(e) => this.handleInputChange('comments', e.target.value)}
                      placeholder="Please share your questions, comments, or how we can help you..."
                      required
                      rows={6}
                      resize="vertical"
                      size="large"
                    />
                  </Field>
                </div>

                {submitSuccess && (
                  <div className="contact-success-message">
                    <p>Thank you for contacting us! We'll get back to you shortly.</p>
                  </div>
                )}

                {submitError && (
                  <div className="contact-error-message">
                    <p>{submitError}</p>
                  </div>
                )}

                <div className="form-actions">
                  <Button 
                    appearance="primary" 
                    size="large"
                    type="submit"
                    disabled={isSubmitting}
                    icon={<SendFilled />}
                    iconPosition="after"
                    className="submit-button"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </div>

            <div className="contact-info-wrapper">
              <div className="contact-info-card">
                <h3>Contact Information</h3>
                
                <div className="info-section">
                  <h4>Athens Office</h4>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>

                <div className="info-section">
                  <h4>Thessaloniki Office</h4>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>

                <div className="info-section">
                  <h4>Crete Office</h4>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>

                <div className="info-section">
                  <h4>Email</h4>
                  <p>info@meta-365.com</p>
                </div>
              </div>

              {/* <div className="contact-support-card">
                <h3>Need Immediate Support?</h3>
                <p>If you're an existing customer and need technical support, visit our helpdesk for faster assistance.</p>
                <Button 
                  appearance="outline" 
                  size="medium"
                  onClick={() => window.location.href = '/helpdesk'}
                >
                  Visit Helpdesk
                </Button>
              </div> */}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default Contact;

